import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid, 
  Paper, 
  Chip,
  Card,
  CardMedia,
  CardContent,
  Divider,
  IconButton,
  CircularProgress,
  Fade,
  useTheme,
  useMediaQuery,
  Stack,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Share as ShareIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { getEventById, EventResponse, EventTier } from '../services/eventService';
import { getTicketsSold, downloadTicketPurchasers } from '../services/ticketService';
import { createCheckoutSession } from '../services/paymentService';
import AppMenu from '../components/AppMenu';
import ComingSoonTag from '../components/ComingSoonTag';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';
import GuestCheckoutModal from '../components/GuestCheckoutModal';
import { PLACEHOLDER_EVENT_IMAGE, DEFAULT_TIMEZONE, PLATFORM_FEE_RATE } from '../config';
import { formatCurrency } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';

interface Event {
  title: string;
  date: string;
  venue: {
    name: string;
    address: string;
    eirCode: string;
  };
  time: {
    doorsOpen: string;
    start: string;
  };
  price: number;
  isFreeOfCharge: boolean;
  duration: string;
  description: string;
  refundPolicy: string;
  booking: {
    date: string;
    time: string;
    tickets: {
      general: number;
      vip: number;
    };
  };
  artist: {
    name: string;
    bio: string;
  };
  heroImage: string;
}

const mapApiResponseToEvent = (apiEvent: EventResponse): Event => {
  const formatDate = (date: Date) => {
    const dayOfWeek = date.toLocaleString('default', { weekday: 'short' });
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${dayOfWeek}, ${day} ${month} ${year}`;
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      timeZone: DEFAULT_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(/am/g, 'AM').replace(/pm/g, 'PM');
  };

  return {
    title: apiEvent.title,
    date: formatDate(new Date(apiEvent.startTime)),
    venue: {
      name: apiEvent.venue,
      address: apiEvent.venue,
      eirCode: apiEvent.eirCode || '',
    },
    time: {
      doorsOpen: formatTime(new Date(apiEvent.startTime)),
      start: formatTime(new Date(apiEvent.startTime)),
    },
    price: apiEvent.sellingPrice,
    isFreeOfCharge: apiEvent.isFreeOfCharge,
    duration: `${Math.round((new Date(apiEvent.endTime).getTime() - new Date(apiEvent.startTime).getTime()) / (1000 * 60 * 60))} hours`,
    description: apiEvent.description,
    refundPolicy: apiEvent.refundPolicy || '',
    booking: {
      date: formatDate(new Date(apiEvent.startTime)),
      time: formatTime(new Date(apiEvent.startTime)),
      tickets: {
        general: apiEvent.sellingPrice,
        vip: apiEvent.anchorPrice,
      },
    },
    artist: {
      name: apiEvent.title.split(' - ')[0] || 'Unknown Artist',
      bio: apiEvent.artistDescription || apiEvent.organizerDescription || 'No biography available',
    },
    heroImage: apiEvent.imageUrl || PLACEHOLDER_EVENT_IMAGE,
  };
};

const roundToTwo = (value: number): number => Math.round(value * 100) / 100;

const EventDetail: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, userId } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [originalEventData, setOriginalEventData] = useState<EventResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Prevent duplicate fetches (e.g., React StrictMode) per eventId
  const fetchOnceRef = React.useRef<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [descriptionOverflows, setDescriptionOverflows] = useState(false);
  const [ticketQuantity, setTicketQuantity] = useState(1);
  /** For tiered paid events: quantity per tierId (only tiers on sale). */
  const [tierQuantities, setTierQuantities] = useState<Record<number, number>>({});
  const [isBooking, setIsBooking] = useState(false);
  const [infoSnackbar, setInfoSnackbar] = useState({ open: false, message: '' });
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [wasTryingToBook, setWasTryingToBook] = useState(false);
  const [isPostLoginFlow, setIsPostLoginFlow] = useState(false);
  const [isGuestCheckoutOpen, setIsGuestCheckoutOpen] = useState(false);
  const [guestCheckoutError, setGuestCheckoutError] = useState<string | null>(null);
  const [ticketsSold, setTicketsSold] = useState(0);
  const [ticketsSoldLoading, setTicketsSoldLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const descriptionRef = React.useRef<HTMLParagraphElement>(null);

  /** Tiers currently on sale (now between onSaleFrom and onSaleUntil) for paid events. */
  const tiersOnSale = useMemo(() => {
    const tiers = originalEventData?.tiers ?? [];
    if (tiers.length === 0) return [];
    const now = Date.now();
    return [...tiers]
      .filter((t) => {
        const from = new Date(t.onSaleFrom).getTime();
        const to = new Date(t.onSaleUntil).getTime();
        return from <= now && now <= to;
      })
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [originalEventData]);

  const hasTiers = !!(originalEventData?.tiers && originalEventData.tiers.length > 0);

  const ticketPricing = useMemo(() => {
    if (!event) {
      return { unitPrice: 0, subtotal: 0, platformFee: 0, total: 0 };
    }
    if (tiersOnSale.length > 0) {
      const subtotalRaw = roundToTwo(
        tiersOnSale.reduce((sum, t) => sum + (tierQuantities[t.id] ?? 0) * t.sellingPrice, 0)
      );
      const platformFeeAmount = roundToTwo(subtotalRaw * PLATFORM_FEE_RATE);
      return {
        unitPrice: 0,
        subtotal: subtotalRaw,
        platformFee: platformFeeAmount,
        total: roundToTwo(subtotalRaw + platformFeeAmount),
      };
    }
    // Legacy / non-tiered events (no tiers at all)
    if (hasTiers) {
      // Event has tiers but none currently on sale – show zeroed summary, no single-price fallback
      return { unitPrice: 0, subtotal: 0, platformFee: 0, total: 0 };
    }
    const unitPrice = event.price || 0;
    const subtotalRaw = roundToTwo(unitPrice * ticketQuantity);
    if (event.isFreeOfCharge || unitPrice === 0) {
      return { unitPrice, subtotal: subtotalRaw, platformFee: 0, total: subtotalRaw };
    }
    const platformFeeAmount = roundToTwo(subtotalRaw * PLATFORM_FEE_RATE);
    return {
      unitPrice,
      subtotal: subtotalRaw,
      platformFee: platformFeeAmount,
      total: roundToTwo(subtotalRaw + platformFeeAmount),
    };
  }, [event, ticketQuantity, tiersOnSale, tierQuantities, hasTiers]);

  // Check if the event is in the past
  const isEventInPast = () => {
    if (!originalEventData) return false;
    const eventEndTime = new Date(originalEventData.endTime);
    const now = new Date();
    return eventEndTime < now;
  };

  const handleLoginClick = () => {
    setIsLoginOpen(true);
  };

  const handleSignupClick = () => {
    setIsSignupOpen(true);
  };

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };

  const handleGuestCheckoutSubmit = async (data: { email: string; firstName: string; lastName: string }) => {
    if (!eventId || !event) return;
    setGuestCheckoutError(null);
    setIsBooking(true);
    try {
      const guestPayload = {
        eventId: parseInt(eventId),
        email: data.email,
        ...(data.firstName && { firstName: data.firstName }),
        ...(data.lastName && { lastName: data.lastName }),
      };
      if (tiersOnSale.length > 0) {
        const items = tiersOnSale
          .filter((t) => (tierQuantities[t.id] ?? 0) > 0)
          .map((t) => ({ tierId: t.id, quantity: tierQuantities[t.id] }));
        if (items.length === 0) {
          setGuestCheckoutError('Select at least one ticket.');
          setIsBooking(false);
          return;
        }
        const checkoutSession = await createCheckoutSession({ ...guestPayload, items });
        localStorage.setItem('stripe_checkout_eventId', eventId);
        window.location.href = checkoutSession.url;
      } else {
        const checkoutSession = await createCheckoutSession({
          ...guestPayload,
          numberOfTickets: ticketQuantity,
        });
        localStorage.setItem('stripe_checkout_eventId', eventId);
        window.location.href = checkoutSession.url;
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to start checkout. Please try again.';
      setGuestCheckoutError(message);
      setIsBooking(false);
    }
  };

  const handleGuestCheckoutSwitchToLogin = () => {
    setIsGuestCheckoutOpen(false);
    setWasTryingToBook(true);
    setIsLoginOpen(true);
  };

  const handleLoginSwitchToGuestCheckout = () => {
    setIsLoginOpen(false);
    setGuestCheckoutError(null);
    setIsGuestCheckoutOpen(true);
  };

  const handleDownloadCSV = async () => {
    if (!eventId) return;
    
    try {
      const blob = await downloadTicketPurchasers(parseInt(eventId));
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ticket-purchasers-${eventId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      // You could add error handling here (e.g., show error toast)
    }
  };

  const handleSuccessfulLogin = () => {
    // Set flag to indicate we're in post-login flow
    setIsPostLoginFlow(true);
    // If user was trying to book, proceed with booking
    if (wasTryingToBook) {
      setWasTryingToBook(false);
      // Small delay to ensure modal is closed and auth state is updated
      setTimeout(() => {
        // Check if user is now authenticated before proceeding
        if (isAuthenticated) {
          handleRSVP();
        }
        // Reset the post-login flow flag
        setIsPostLoginFlow(false);
      }, 200);
    } else {
      // Reset the post-login flow flag immediately if not booking
      setIsPostLoginFlow(false);
    }
  };

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  const handleRSVP = async () => {
    if (!eventId || !event) return;
    
    if (isEventInPast()) {
      setInfoSnackbar({
        open: true,
        message: 'This event has already ended. Booking is no longer available.'
      });
      return;
    }
    
    if (!isAuthenticated && !isPostLoginFlow) {
      setWasTryingToBook(true);
      setIsLoginOpen(true);
      return;
    }
    
    if (tiersOnSale.length > 0) {
      const items = tiersOnSale
        .filter((t) => (tierQuantities[t.id] ?? 0) > 0)
        .map((t) => ({ tierId: t.id, quantity: tierQuantities[t.id] }));
      if (items.length === 0) {
        setInfoSnackbar({ open: true, message: 'Select at least one ticket.' });
        return;
      }
      setIsBooking(true);
      try {
        const checkoutSession = await createCheckoutSession({
          eventId: parseInt(eventId),
          items,
        });
        localStorage.setItem('stripe_checkout_eventId', eventId);
        window.location.href = checkoutSession.url;
      } catch (error: any) {
        console.error('Error starting checkout:', error);
        const message = error.response?.data?.message || error.response?.data?.error || 'Failed to process booking. Please try again.';
        setInfoSnackbar({ open: true, message });
        setIsBooking(false);
      }
      return;
    }
    
    setIsBooking(true);
    try {
      const checkoutSession = await createCheckoutSession({
        eventId: parseInt(eventId),
        numberOfTickets: ticketQuantity,
      });
      localStorage.setItem('stripe_checkout_eventId', eventId);
      window.location.href = checkoutSession.url;
    } catch (error: any) {
      console.error('Error starting checkout:', error);
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to process booking. Please try again.';
      setInfoSnackbar({ open: true, message });
      setIsBooking(false);
    }
  };

  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!eventId) {
        setError('Event ID is required');
        setLoading(false);
        return;
      }
      // Guard: only fetch once per eventId in this mount cycle
      if (fetchOnceRef.current === eventId) return;
      fetchOnceRef.current = eventId;

      try {
        const eventData = await getEventById(eventId);
        setOriginalEventData(eventData);
        setEvent(mapApiResponseToEvent(eventData));
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load event details');
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetails();
  }, [eventId]);

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if description overflows after event data is loaded
  useEffect(() => {
    if (descriptionRef.current && event) {
      const element = descriptionRef.current;
      const lineHeight = parseInt(window.getComputedStyle(element).lineHeight);
      const maxHeight = lineHeight * 3; // 3 lines when collapsed
      const actualHeight = element.scrollHeight;
      setDescriptionOverflows(actualHeight > maxHeight);
    }
  }, [event]);

  // Fetch tickets sold data for event creator
  useEffect(() => {
    const fetchTicketsSold = async () => {
      if (!eventId || !isAuthenticated || !userId || !originalEventData || originalEventData.creatorId !== userId) {
        return;
      }

      setTicketsSoldLoading(true);
      try {
        const soldCount = await getTicketsSold(parseInt(eventId));
        setTicketsSold(soldCount);
      } catch (error) {
        console.error('Error fetching tickets sold:', error);
        setTicketsSold(0);
      } finally {
        setTicketsSoldLoading(false);
      }
    };

    fetchTicketsSold();
  }, [eventId, isAuthenticated, userId, originalEventData]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error || !event) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography color="error" variant="h6">{error || 'Event not found'}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <AppMenu 
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
        currentPath={`/events/${eventId}`}
      />
      
      <Container maxWidth="lg" sx={{ px: { xs: 0, md: 3 }, pt: { xs: 0, md: 4 } }}>
        <Box
          sx={{
            position: 'relative',
            height: { xs: 'auto', md: '500px' },
            minHeight: { xs: '200px', md: 'auto' },
          }}
        >
          <Box
            component="img"
            src={event.heroImage}
            alt={event.title}
            sx={{
              width: '100%',
              height: { xs: 'auto', md: '100%' },
              maxHeight: { xs: '680px', md: 'none' },
              objectFit: { xs: 'contain', md: 'cover' },
              backgroundColor: { xs: 'common.black', md: 'transparent' },
              borderRadius: { xs: 0, md: 2 },
            }}
          />


        {/* Edit Button Overlay - Only show for event creator */}
        {isAuthenticated && userId && originalEventData && originalEventData.creatorId === userId && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 20,
              right: 20,
            }}
          >
            <Button
              variant="contained"
              size="medium"
              onClick={() => navigate(`/events/${eventId}/edit`)}
              startIcon={<EditIcon />}
              sx={{
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.8)',
                },
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
              }}
            >
              EDIT
            </Button>
          </Box>
        )}
        </Box>
      </Container>

      {/* Mobile Ticket Details Box - Full Width Below Hero Image */}
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 0 }, display: { xs: 'block', md: 'none' } }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            bgcolor: 'background.paper',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Stack spacing={3}>
            {/* Event Title */}
            <Typography 
              variant="h5" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                color: 'text.primary',
                fontSize: { xs: '1.25rem', md: '1.5rem' }
              }}
            >
              {event.title}
            </Typography>
            
            {/* Event Details */}
            <Stack spacing={2}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <LocationIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {event.venue.name}{event.venue.eirCode ? `, ${event.venue.eirCode}` : ''}
                </Typography>
              </Box>
              
              <Box display="flex" alignItems="center" gap={1.5}>
                <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {event.date}
                </Typography>
              </Box>
              
              <Box display="flex" alignItems="center" gap={1.5}>
                <AccessTimeIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Doors Open: {event.time.doorsOpen}
                </Typography>
              </Box>
              

            </Stack>
            
            <Divider />
            
            {/* Tiered pricing (paid events with tiers) */}
            {!event.isFreeOfCharge && hasTiers && tiersOnSale.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Select tickets</Typography>
                {tiersOnSale.map((tier) => {
                  const qty = tierQuantities[tier.id] ?? 0;
                  const totalAcrossTiers = tiersOnSale.reduce((sum, t) => sum + (tierQuantities[t.id] ?? 0), 0);
                  return (
                    <Box key={tier.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box>
                        <Typography variant="body1" fontWeight={600}>{tier.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{formatCurrency(tier.sellingPrice)} each</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                        <IconButton size="small" onClick={() => setTierQuantities((prev) => ({ ...prev, [tier.id]: Math.max(0, (prev[tier.id] ?? 0) - 1) }))} disabled={qty <= 0} sx={{ borderRadius: '8px 0 0 8px' }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>−</Typography>
                        </IconButton>
                        <Typography variant="body1" sx={{ px: 2, minWidth: 32, textAlign: 'center', fontWeight: 600 }}>{qty}</Typography>
                        <IconButton size="small" onClick={() => setTierQuantities((prev) => ({ ...prev, [tier.id]: (prev[tier.id] ?? 0) + 1 }))} disabled={totalAcrossTiers >= 4} sx={{ borderRadius: '0 8px 8px 0' }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>+</Typography>
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
                <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">Tickets subtotal</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatCurrency(ticketPricing.subtotal)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">Platform fee ({Math.round(PLATFORM_FEE_RATE * 100)}%)</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatCurrency(ticketPricing.platformFee)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" pt={1} borderTop="1px dashed" borderColor="divider">
                    <Typography variant="body2" fontWeight={600}>Total due today</Typography>
                    <Typography variant="body2" fontWeight={700}>{formatCurrency(ticketPricing.total)}</Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Max. 4 tickets total. The 5% platform fee helps us keep MyEventShop running.</Typography>
              </Box>
            )}

            {/* Paid event with tiers but none currently on sale */}
            {!event.isFreeOfCharge && hasTiers && tiersOnSale.length === 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Ticket sales are not currently open for this event. Please check back later.
                </Typography>
              </Box>
            )}

            {/* Single price (paid events without tiers / legacy) */}
            {!event.isFreeOfCharge && !hasTiers && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textAlign: 'center' }}>Price</Typography>
                    {event.booking.tickets.vip && event.booking.tickets.vip !== event.price ? (
                      <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.secondary', textDecoration: 'line-through', opacity: 0.7 }}>{formatCurrency(event.booking.tickets.vip)}</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: '#EC4899' }}>{formatCurrency(event.price)}</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, display: 'block', mt: 0.5 }}>Save {formatCurrency(event.booking.tickets.vip - event.price)}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>{formatCurrency(event.price)}</Typography>
                    )}
                    <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">Tickets subtotal</Typography>
                        <Typography variant="body2" color="text.primary" fontWeight={600}>{formatCurrency(ticketPricing.subtotal)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">Platform fee ({Math.round(PLATFORM_FEE_RATE * 100)}%)</Typography>
                        <Typography variant="body2" color="text.primary" fontWeight={600}>{formatCurrency(ticketPricing.platformFee)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" pt={1} borderTop="1px dashed" borderColor="divider">
                        <Typography variant="body2" color="text.primary" fontWeight={600}>Total due today</Typography>
                        <Typography variant="body2" color="text.primary" fontWeight={700}>{formatCurrency(ticketPricing.total)}</Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>The 5% platform fee helps us keep MyEventShop running. It is only applied to paid bookings.</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textAlign: 'center' }}>Qty</Typography>
                    <Box display="flex" alignItems="center" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                      <IconButton size="small" onClick={() => setTicketQuantity(Math.max(1, ticketQuantity - 1))} disabled={ticketQuantity <= 1} sx={{ borderRadius: '8px 0 0 8px', '&:hover': { bgcolor: 'action.hover' } }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>-</Typography>
                      </IconButton>
                      <Typography variant="body1" sx={{ px: 3, py: 1, minWidth: 60, textAlign: 'center', fontWeight: 600 }}>{ticketQuantity}</Typography>
                      <IconButton size="small" onClick={() => setTicketQuantity(Math.min(4, ticketQuantity + 1))} disabled={ticketQuantity >= 4} sx={{ borderRadius: '0 8px 8px 0', '&:hover': { bgcolor: 'action.hover' } }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>+</Typography>
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Max. 4</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            
            {/* Free Event Ticket Quantity */}
            {event.isFreeOfCharge && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textAlign: 'center' }}>
                      Free Event
                    </Typography>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 700,
                        color: 'success.main'
                      }}
                    >
                      FREE
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      No platform fee is applied to free bookings.
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textAlign: 'center' }}>
                      Qty
                    </Typography>
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => setTicketQuantity(Math.max(1, ticketQuantity - 1))}
                        disabled={ticketQuantity <= 1}
                        sx={{ 
                          borderRadius: '8px 0 0 8px',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>-</Typography>
                      </IconButton>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          px: 3,
                          py: 1,
                          minWidth: 60,
                          textAlign: 'center',
                          fontWeight: 600
                        }}
                      >
                        {ticketQuantity}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setTicketQuantity(Math.min(4, ticketQuantity + 1))}
                        disabled={ticketQuantity >= 4}
                        sx={{ 
                          borderRadius: '0 8px 8px 0',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>+</Typography>
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                      >
                        Max. 4
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            
            {/* Hide book button when event has tiers but none are on sale */}
            {!(hasTiers && tiersOnSale.length === 0) && (
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleRSVP}
                disabled={isBooking || isEventInPast()}
                sx={{ 
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  bgcolor: '#EC4899',
                  mt: '0 !important',
                  '&:hover': {
                    bgcolor: '#DB2777'
                  },
                  '&:disabled': {
                    bgcolor: 'action.disabled',
                    color: 'action.disabledBackground'
                  }
                }}
              >
                {isBooking ? 'BOOKING...' : 
                 isEventInPast() ? 'EVENT ENDED' : 
                 (event.isFreeOfCharge ? 'BOOK SLOT' : 'BOOK')}
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>



      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
        <Grid container spacing={4}>
          {/* Left Column - Event Information */}
          <Grid item xs={12} md={8}>
            <Stack spacing={4}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: { xs: 2, md: 4 },
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    color="primary"
                    fontWeight="bold" 
                  >
                    About the event
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Share">
                      <IconButton 
                        onClick={() => {
                          setInfoSnackbar({ open: true, message: 'Share feature coming soon!' });
                        }}
                      >
                        <ShareIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Add to favorites">
                      <IconButton 
                        onClick={() => {
                          setInfoSnackbar({ open: true, message: 'Add to favorites feature coming soon!' });
                        }}
                      >
                        <FavoriteBorderIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
                
                {/* Desktop Layout - All chips in one row */}
                <Stack 
                  direction="row" 
                  spacing={2} 
                  alignItems="center"
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    flexWrap: 'nowrap',
                    gap: 2,
                    mb: 3,
                    overflowX: 'auto',
                    pb: 1,
                    '&::-webkit-scrollbar': {
                      height: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#EC4899',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#DB2777',
                    }
                  }}
                >
                  <Chip
                    icon={<LocationIcon />}
                    label={event.venue.eirCode ? `${event.venue.name}, ${event.venue.eirCode}` : event.venue.name}
                    sx={{ 
                      bgcolor: '#EC4899',
                      color: 'white',
                      '& .MuiChip-icon': { 
                        color: 'white',
                        fontSize: '1.1rem',
                        ml: 1.5
                      },
                      '& .MuiChip-label': {
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        px: 1.5,
                        letterSpacing: '0.3px',
                        whiteSpace: 'nowrap'
                      },
                      height: 36,
                      borderRadius: '20px',
                      '&:hover': {
                        bgcolor: '#DB2777'
                      }
                    }}
                  />
                  <Chip
                    icon={<CalendarIcon />}
                    label={event.date}
                    sx={{ 
                      bgcolor: '#EC4899',
                      color: 'white',
                      '& .MuiChip-icon': { 
                        color: 'white',
                        fontSize: '1.1rem',
                        ml: 1.5
                      },
                      '& .MuiChip-label': {
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        px: 1.5,
                        letterSpacing: '0.3px',
                        whiteSpace: 'nowrap'
                      },
                      height: 36,
                      borderRadius: '20px',
                      '&:hover': {
                        bgcolor: '#DB2777'
                      }
                    }}
                  />
                  <Chip
                    icon={<AccessTimeIcon />}
                    label={`${event.time.doorsOpen}`}
                    sx={{ 
                      bgcolor: '#EC4899',
                      color: 'white',
                      '& .MuiChip-icon': { 
                        color: 'white',
                        fontSize: '1.1rem',
                        ml: 1.5
                      },
                      '& .MuiChip-label': {
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        px: 1.5,
                        letterSpacing: '0.3px',
                        whiteSpace: 'nowrap'
                      },
                      height: 36,
                      borderRadius: '20px',
                      '&:hover': {
                        bgcolor: '#DB2777'
                      }
                    }}
                  />
                </Stack>

                {/* Mobile Layout - Each chip on its own line */}
                <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 3 }}>
                  <Stack spacing={2}>
                    {/* Venue */}
                    <Box>
                      <Chip
                        icon={<LocationIcon />}
                        label={event.venue.eirCode ? `${event.venue.name}, ${event.venue.eirCode}` : event.venue.name}
                        sx={{ 
                          bgcolor: '#EC4899',
                          color: 'white',
                          '& .MuiChip-icon': { 
                            color: 'white',
                            fontSize: '1.1rem',
                            ml: 1
                          },
                          '& .MuiChip-label': {
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            px: 1,
                            letterSpacing: '0.3px',
                            whiteSpace: 'nowrap'
                          },
                          height: 36,
                          borderRadius: '20px',
                          '&:hover': {
                            bgcolor: '#DB2777'
                          }
                        }}
                      />
                    </Box>
                    
                    {/* Date */}
                    <Box>
                      <Chip
                        icon={<CalendarIcon />}
                        label={event.date}
                        sx={{ 
                          bgcolor: '#EC4899',
                          color: 'white',
                          '& .MuiChip-icon': { 
                            color: 'white',
                            fontSize: '1.1rem',
                            ml: 1
                          },
                          '& .MuiChip-label': {
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            px: 1,
                            letterSpacing: '0.3px',
                            whiteSpace: 'nowrap'
                          },
                          height: 36,
                          borderRadius: '20px',
                          '&:hover': {
                            bgcolor: '#DB2777'
                          }
                        }}
                      />
                    </Box>
                    
                    {/* Time */}
                    <Box>
                      <Chip
                        icon={<AccessTimeIcon />}
                        label={`${event.time.doorsOpen}`}
                        sx={{ 
                          bgcolor: '#EC4899',
                          color: 'white',
                          '& .MuiChip-icon': { 
                            color: 'white',
                            fontSize: '1.1rem',
                            ml: 1
                          },
                          '& .MuiChip-label': {
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            px: 1,
                            letterSpacing: '0.3px',
                            whiteSpace: 'nowrap'
                          },
                          height: 36,
                          borderRadius: '20px',
                          '&:hover': {
                            bgcolor: '#DB2777'
                          }
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Typography 
                  ref={descriptionRef}
                  variant="body1" 
                  paragraph
                  sx={{ 
                    lineHeight: 1.8,
                    color: 'text.secondary',
                    display: '-webkit-box',
                    WebkitLineClamp: showFullDescription ? 'unset' : 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  {event.description}
                </Typography>
                {descriptionOverflows && (
                  <Button 
                    variant="outlined" 
                    color="primary"
                    onClick={toggleDescription}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {showFullDescription ? 'Show less' : 'Read more'}
                  </Button>
                )}
              </Paper>

              {event.artist.bio && event.artist.bio !== 'No biography available' && (
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: { xs: 2, md: 4 },
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                  }}
                >
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    color="primary"
                    fontWeight="bold"
                    mb={3}
                  >
                    Artist Description
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {event.artist.bio}
                  </Typography>
                </Paper>
              )}

              {/* Refund Policy Section */}
              {event.refundPolicy && (
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: { xs: 2, md: 4 },
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                  }}
                >
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    color="primary"
                    fontWeight="bold"
                    mb={3}
                  >
                    Refund Policy
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
                  >
                    {event.refundPolicy}
                  </Typography>
                </Paper>
              )}

              {/* Booking Statistics Section - Only show for event creator */}
              {isAuthenticated && userId && originalEventData && originalEventData.creatorId === userId && (
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: { xs: 2, md: 4 },
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography 
                      variant="h5" 
                      component="h2" 
                      color="primary"
                      fontWeight="bold"
                    >
                      Booking Statistics
                    </Typography>
                  </Box>
                  
                  {ticketsSoldLoading ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Box>
                      <Typography 
                        variant="body1" 
                        color="text.secondary"
                        sx={{ lineHeight: 1.6, mb: 3 }}
                      >
                        Track your event's performance with real-time booking data. This information is only for the event creator and not for public view.
                      </Typography>
                      
                      <Grid container spacing={3} mb={3}>
                        <Grid item xs={12} sm={6}>
                          <Box 
                            sx={{ 
                              p: 3, 
                              bgcolor: 'primary.50', 
                              borderRadius: 2,
                              textAlign: 'center',
                              border: '1px solid',
                              borderColor: 'primary.200'
                            }}
                          >
                            <Typography variant="h4" color="primary" fontWeight="bold">
                              {originalEventData.capacity}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Capacity
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <Box 
                            sx={{ 
                              p: 3, 
                              bgcolor: 'success.50', 
                              borderRadius: 2,
                              textAlign: 'center',
                              border: '1px solid',
                              borderColor: 'success.200'
                            }}
                          >
                            <Typography variant="h4" color="success.main" fontWeight="bold">
                              {ticketsSold}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Tickets Sold
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={handleDownloadCSV}
                          startIcon={<DownloadIcon />}
                          sx={{ 
                            textTransform: 'none',
                            px: 3,
                            py: 1.5
                          }}
                        >
                          Download Ticket Purchasers List
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}
            </Stack>
          </Grid>

          {/* Right Column - Ticket Details Box (Desktop Only) */}
          <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                border: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 20
              }}
            >
              <Stack spacing={3}>
                {/* Event Title */}
                <Typography 
                  variant="h5" 
                  component="h2" 
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                  }}
                >
                  {event.title}
                </Typography>
                
                {/* Event Details */}
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <LocationIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {event.venue.name}{event.venue.eirCode ? `, ${event.venue.eirCode}` : ''}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {event.date}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <AccessTimeIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Doors Open: {event.time.doorsOpen}
                    </Typography>
                  </Box>
                  

                </Stack>
                
                <Divider />
                
                {/* Tiered pricing (desktop) */}
                {!event.isFreeOfCharge && hasTiers && tiersOnSale.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Select tickets</Typography>
                    {tiersOnSale.map((tier) => {
                      const qty = tierQuantities[tier.id] ?? 0;
                      const totalAcrossTiers = tiersOnSale.reduce((sum, t) => sum + (tierQuantities[t.id] ?? 0), 0);
                      return (
                        <Box key={tier.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Box>
                            <Typography variant="body1" fontWeight={600}>{tier.name}</Typography>
                            <Typography variant="body2" color="text.secondary">{formatCurrency(tier.sellingPrice)} each</Typography>
                          </Box>
                          <Box display="flex" alignItems="center" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                            <IconButton size="small" onClick={() => setTierQuantities((prev) => ({ ...prev, [tier.id]: Math.max(0, (prev[tier.id] ?? 0) - 1) }))} disabled={qty <= 0} sx={{ borderRadius: '8px 0 0 8px' }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>−</Typography>
                            </IconButton>
                            <Typography variant="body1" sx={{ px: 2, minWidth: 32, textAlign: 'center', fontWeight: 600 }}>{qty}</Typography>
                            <IconButton size="small" onClick={() => setTierQuantities((prev) => ({ ...prev, [tier.id]: (prev[tier.id] ?? 0) + 1 }))} disabled={totalAcrossTiers >= 4} sx={{ borderRadius: '0 8px 8px 0' }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>+</Typography>
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })}
                    <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">Tickets subtotal</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatCurrency(ticketPricing.subtotal)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">Platform fee ({Math.round(PLATFORM_FEE_RATE * 100)}%)</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatCurrency(ticketPricing.platformFee)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" pt={1} borderTop="1px dashed" borderColor="divider">
                        <Typography variant="body2" fontWeight={600}>Total due today</Typography>
                        <Typography variant="body2" fontWeight={700}>{formatCurrency(ticketPricing.total)}</Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Max. 4 tickets total.</Typography>
                  </Box>
                )}

                {/* Paid event with tiers but none currently on sale (desktop) */}
                {!event.isFreeOfCharge && hasTiers && tiersOnSale.length === 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Ticket sales are not currently open for this event. Please check back later.
                    </Typography>
                  </Box>
                )}

                {/* Single price paid (desktop) */}
                {!event.isFreeOfCharge && !hasTiers && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box sx={{ flex: 1, pr: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textAlign: 'center' }}>Price</Typography>
                        {event.booking.tickets.vip && event.booking.tickets.vip !== event.price ? (
                          <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.secondary', textDecoration: 'line-through', opacity: 0.7 }}>{formatCurrency(event.booking.tickets.vip)}</Typography>
                              <Typography variant="h4" sx={{ fontWeight: 700, color: '#EC4899' }}>{formatCurrency(event.price)}</Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, display: 'block', mt: 0.5 }}>Save {formatCurrency(event.booking.tickets.vip - event.price)}</Typography>
                          </Box>
                        ) : (
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>{formatCurrency(event.price)}</Typography>
                        )}
                        <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" color="text.secondary">Tickets subtotal</Typography>
                            <Typography variant="body2" color="text.primary" fontWeight={600}>{formatCurrency(ticketPricing.subtotal)}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" color="text.secondary">Platform fee ({Math.round(PLATFORM_FEE_RATE * 100)}%)</Typography>
                            <Typography variant="body2" color="text.primary" fontWeight={600}>{formatCurrency(ticketPricing.platformFee)}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" pt={1} borderTop="1px dashed" borderColor="divider">
                            <Typography variant="body2" color="text.primary" fontWeight={600}>Total due today</Typography>
                            <Typography variant="body2" color="text.primary" fontWeight={700}>{formatCurrency(ticketPricing.total)}</Typography>
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>The 5% platform fee helps us keep MyEventShop running.</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textAlign: 'center' }}>Qty</Typography>
                        <Box display="flex" alignItems="center" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                          <IconButton size="small" onClick={() => setTicketQuantity(Math.max(1, ticketQuantity - 1))} disabled={ticketQuantity <= 1} sx={{ borderRadius: '8px 0 0 8px', '&:hover': { bgcolor: 'action.hover' } }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>-</Typography>
                          </IconButton>
                          <Typography variant="body1" sx={{ px: 2, py: 1, minWidth: 40, textAlign: 'center', fontWeight: 600 }}>{ticketQuantity}</Typography>
                          <IconButton size="small" onClick={() => setTicketQuantity(Math.min(4, ticketQuantity + 1))} disabled={ticketQuantity >= 4} sx={{ borderRadius: '0 8px 8px 0', '&:hover': { bgcolor: 'action.hover' } }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>+</Typography>
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Max. 4</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
                
                {/* Free Event Ticket Quantity */}
                {event.isFreeOfCharge && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box sx={{ flex: 1, pr: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textAlign: 'center' }}>
                          Free Event
                        </Typography>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: 700,
                            color: 'success.main'
                          }}
                        >
                          FREE
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: 'block' }}
                        >
                          No platform fee is applied to free bookings.
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textAlign: 'center' }}>
                          Qty
                        </Typography>
                        <Box 
                          display="flex" 
                          alignItems="center" 
                          sx={{ 
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'background.paper'
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => setTicketQuantity(Math.max(1, ticketQuantity - 1))}
                            disabled={ticketQuantity <= 1}
                            sx={{ 
                              borderRadius: '8px 0 0 8px',
                              '&:hover': {
                                bgcolor: 'action.hover'
                              }
                            }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>-</Typography>
                          </IconButton>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              px: 3,
                              py: 1,
                              minWidth: 60,
                              textAlign: 'center',
                              fontWeight: 600
                            }}
                          >
                            {ticketQuantity}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setTicketQuantity(Math.min(4, ticketQuantity + 1))}
                            disabled={ticketQuantity >= 4}
                            sx={{ 
                              borderRadius: '0 8px 8px 0',
                              '&:hover': {
                                bgcolor: 'action.hover'
                              }
                            }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>+</Typography>
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                          >
                            Max. 4
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
                
                {/* Hide book button when event has tiers but none are on sale */}
                {!(hasTiers && tiersOnSale.length === 0) && (
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleRSVP}
                    disabled={isBooking || isEventInPast()}
                    sx={{ 
                      py: 1.5,
                      px: 3,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      bgcolor: '#EC4899',
                      mt: '0 !important',
                      '&:hover': {
                        bgcolor: '#DB2777'
                      },
                      '&:disabled': {
                        bgcolor: 'action.disabled',
                        color: 'action.disabledBackground'
                      }
                    }}
                  >
                    {isBooking ? 'BOOKING...' : 
                     isEventInPast() ? 'EVENT ENDED' : 
                     (event.isFreeOfCharge ? 'BOOK SLOT' : 'BOOK')}
                  </Button>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <GuestCheckoutModal
        open={isGuestCheckoutOpen}
        onClose={() => { setIsGuestCheckoutOpen(false); setGuestCheckoutError(null); }}
        onSubmit={handleGuestCheckoutSubmit}
        onSwitchToLogin={handleGuestCheckoutSwitchToLogin}
        isLoading={isBooking}
        error={guestCheckoutError}
        isFreeEvent={event?.isFreeOfCharge ?? false}
      />

      <LoginModal
        open={isLoginOpen}
        onClose={() => setIsLoginOpen(false)}
        onSwitchToSignup={handleSwitchToSignup}
        redirectPath={`/events/${eventId}`}
        onSuccess={handleSuccessfulLogin}
        showGuestCheckoutLink
        onGuestCheckout={handleLoginSwitchToGuestCheckout}
      />

      <SignupModal
        open={isSignupOpen}
        onClose={() => setIsSignupOpen(false)}
        onSwitchToLogin={handleSwitchToLogin}
      />

      {/* Info Snackbar for Coming Soon Features */}
      <Snackbar
        open={infoSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setInfoSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setInfoSnackbar({ open: false, message: '' })} 
          severity="info" 
          variant="filled"
          sx={{ 
            width: '100%',
            bgcolor: '#EC4899', // Custom pink color
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          {infoSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EventDetail; 